import { useSyncExternalStore } from 'react';
import { JOB_STEP, RESUME_REASON, currentJob } from '@nu/shared';
import { outbox } from '../../offline/index.js';
import { queuePhotos } from '../../offline/photos.js';
import { buzz, currentPosition } from '../../native/device.js';

// What this phone has done to the day's jobs, so a tap shows at once with or
// without signal. Each tap is also queued in the outbox; once the server's
// copy has caught up, the local one no longer matters.
//   progress  { [jobId]: { status, at } }   On route / In progress / Aborted
//                                            (with reason), or a job set aside or
//                                            circled back (status 'assigned', reset)
//   next      { [day]: { bookingId, at } }   the job chosen to do next
//   check     { [jobId]: { ok, at } }        back after a pause: site as left?
const KEY = 'nu.jobLocal';
const listeners = new Set();
let cache = null;

function readAll() {
  if (cache) return cache;
  try { cache = JSON.parse(localStorage.getItem(KEY) || 'null'); } catch { cache = null; }
  if (!cache || typeof cache !== 'object') cache = {};
  cache = { progress: cache.progress || {}, next: cache.next || {}, check: cache.check || {} };
  return cache;
}
function writeAll(next) {
  // Keep a week.
  const fresh = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => Date.now() - Date.parse(v.at) < 7 * 864e5));
  cache = { progress: fresh(next.progress), next: fresh(next.next), check: fresh(next.check || {}) };
  try { localStorage.setItem(KEY, JSON.stringify(cache)); } catch { /* storage blocked: lasts this visit */ }
  listeners.forEach((fn) => fn());
}
const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

// Re-render when something is tapped (in this tab).
export function useLocalJobs() {
  return useSyncExternalStore(subscribe, readAll);
}

// The job as this phone knows it: the server's copy, moved on by any tap not
// yet reflected there (and moved back if this phone set it aside).
export function withProgress(job, local = readAll()) {
  if (!job) return job;
  return withCheck(moved(job, local), local);
}

// Answered "site as you left it?" here, before the server has it.
function withCheck(job, local) {
  const chk = local.check?.[job.id];
  if (!chk || !job.pausedAt) return job;
  const paused = Date.parse(job.pausedAt);
  if (job.siteCheckedAt && Date.parse(job.siteCheckedAt) >= paused) return job;
  if (Date.parse(chk.at) < paused - 1000) return job;
  return { ...job, siteCheckedAt: chk.at, raResetAt: chk.ok ? job.raResetAt : chk.at };
}

// The server moved the job back or closed it (the other engineer paused it,
// circled it back, finished it...) after this phone's tap: the server is right.
function changedSince(job, at) {
  const t = Date.parse(at) + 1000;
  return [job.pausedAt, job.skippedAt, job.finishedAt].some((v) => v && Date.parse(v) > t);
}

function moved(job, local) {
  const mine = local.progress[job.id];
  if (!mine) return job;
  if (changedSince(job, mine.at)) return job;
  if (mine.pause) {
    const pausedSince = job.pausedAt ? Date.parse(job.pausedAt) : 0;
    return job.status === 'in_progress' && pausedSince < Date.parse(mine.at) - 1000
      ? { ...job, status: 'paused', statusReason: mine.reason, pausedAt: mine.at, pauses: (job.pauses || 0) + 1, skippedAt: mine.at }
      : job;
  }
  if (mine.reset) {
    const onRouteSince = job.onRouteAt ? Date.parse(job.onRouteAt) : 0;
    return (job.status === 'on_the_way' || job.status === 'in_progress') && onRouteSince <= Date.parse(mine.at) + 1000
      ? { ...job, status: 'assigned', onRouteAt: null, startedAt: null, skippedAt: mine.at, circleBacks: (job.circleBacks || 0) + (mine.circle ? 1 : 0) }
      : job;
  }
  if (JOB_STEP[job.status] >= JOB_STEP[mine.status]) return job;
  return {
    ...job,
    status: mine.status,
    skippedAt: null,
    ...(job.status === 'paused' ? { statusReason: null } : {}),
    onRouteAt: job.onRouteAt || mine.at,
    startedAt: mine.status === 'in_progress' ? job.startedAt || mine.at : job.startedAt,
    ...(mine.reason ? { statusReason: mine.reason, finishedAt: mine.at } : {}),
    ...(mine.status === 'completed' ? { statusReason: null, finishedAt: mine.at } : {}),
  };
}

// The day's jobs as this phone knows them, and the one the team is on now.
export function dayView(data, local) {
  const jobs = (data?.jobs || []).map((j) => withProgress(j, local));
  // The job chosen to do next: this phone's choice while it's still on its
  // way to the server (made after the schedule was loaded), else the
  // server's, which includes the other engineer's switches.
  const mine = local.next[data?.day];
  const pending = mine && (!data?.loadedAt || Date.parse(mine.at) > Date.parse(data.loadedAt));
  let chosen = pending ? mine.bookingId : data?.currentId ?? null;
  // Circled back or paused on this phone since it was chosen, and the
  // server doesn't know yet: the choice is used up. (Once the server knows,
  // its choice already allows for it, and may be newer, e.g. the other
  // engineer choosing the paused job again.)
  const back = local.progress[chosen];
  const since = pending ? mine.at : data?.loadedAt;
  if ((back?.circle || back?.pause) && (!since || Date.parse(back.at) > Date.parse(since))) chosen = null;
  return { jobs, current: currentJob(jobs, chosen) };
}

export async function markJob(job, status) {
  const at = new Date().toISOString();
  const all = readAll();
  writeAll({ ...all, progress: { ...all.progress, [job.id]: { status, at } } });
  await outbox.enqueue('job.status', { bookingId: job.id, status, at });
  buzz();
  // Where they were (proof of attendance), separately: a slow or refused
  // GPS fix never holds the step up.
  const where = await currentPosition();
  if (where) await outbox.enqueue('job.where', { bookingId: job.id, kind: status, ...where, at });
}

// Do `job` next instead of `from` (the current job, if any), with a reason.
export async function switchTo(day, job, from, reason, note) {
  const at = new Date().toISOString();
  const all = readAll();
  const progress = { ...all.progress };
  if (from && JOB_STEP[from.status] === 1) progress[from.id] = { status: 'assigned', at, reset: true };
  writeAll({ progress, next: { ...all.next, [day]: { bookingId: job.id, at } } });
  await outbox.enqueue('job.switch', { bookingId: job.id, reason, note: note || null, at });
}

const whyOf = (reason, note) => (reason === 'Other' ? note : note ? `${reason}: ${note}` : reason);

// On site and can't install: closed as Aborted (photos optional).
export async function abortJob(job, reason, note, photos = []) {
  const at = new Date().toISOString();
  const all = readAll();
  writeAll({ ...all, progress: { ...all.progress, [job.id]: { status: 'aborted', at, reason: whyOf(reason, note) } } });
  await outbox.enqueue('job.abort', { bookingId: job.id, reason, note: note || null, at });
  await queuePhotos(job.id, photos, 'abort', at);
}

// On site and can't install yet: back to Not started, last in the day.
export async function circleBackJob(day, job, reason, note, photos = []) {
  const at = new Date().toISOString();
  const all = readAll();
  const next = { ...all.next };
  if (next[day]?.bookingId === job.id) delete next[day];
  writeAll({ progress: { ...all.progress, [job.id]: { status: 'assigned', at, reset: true, circle: true } }, next });
  await outbox.enqueue('job.circle', { bookingId: job.id, reason, note: note || null, at });
  await queuePhotos(job.id, photos, 'circle_back', at);
}

// Part way through, stop and come back later: Paused, last in the day,
// everything done so far kept.
export async function pauseJob(day, job, reason, note) {
  const at = new Date().toISOString();
  const all = readAll();
  const next = { ...all.next };
  if (next[day]?.bookingId === job.id) delete next[day];
  writeAll({ ...all, progress: { ...all.progress, [job.id]: { status: 'paused', at, pause: true, reason: whyOf(reason, note) } }, next });
  await outbox.enqueue('job.pause', { bookingId: job.id, reason, note: note || null, at });
  buzz();
}

// Back on site after a pause: is it as they left it? No means the risk
// assessment is done again.
export async function checkSite(job, ok) {
  const at = new Date().toISOString();
  const all = readAll();
  writeAll({ ...all, check: { ...all.check, [job.id]: { ok, at } } });
  await outbox.enqueue('job.site_check', { bookingId: job.id, ok, at });
}

// The works are done: Completed, with the stock used ([{ itemId, qty }]).
export async function completeJob(job, notes = '', stock = []) {
  const at = new Date().toISOString();
  const all = readAll();
  writeAll({ ...all, progress: { ...all.progress, [job.id]: { status: 'completed', at, reason: null } } });
  await outbox.enqueue('job.complete', { bookingId: job.id, notes: notes.trim() || null, stock, at });
  buzz();
}

// Back to a paused job: it becomes the team's job (no reason needed) and
// they're On route to it.
export async function resumeJob(day, job, current) {
  if (!current || current.id !== job.id) await switchTo(day, job, current, RESUME_REASON);
  await markJob(job, 'on_the_way');
}
